import { EditorState, Modifier } from 'draft-js';

/**
 * Inserts a variable into a text field at the cursor position
 */
const insertIntoTextField = ({
  value,
  inputRef,
  onChange,
  currentValue,
}: {
  value: string;
  inputRef: React.RefObject<HTMLInputElement>;
  onChange: (newValue: string) => void;
  currentValue: string;
}) => {
  const input = inputRef.current;
  if (!input) {
    onChange(`${currentValue}${value}`);
    return;
  }

  const start = input.selectionStart ?? currentValue.length;
  const end = input.selectionEnd ?? currentValue.length;

  const newValue = `${currentValue.slice(
    0,
    start
  )}${value}${currentValue.slice(end)}`;

  onChange(newValue);

  setTimeout(() => {
    const newPosition = start + value.length;
    input.setSelectionRange(newPosition, newPosition);
    input.focus();
  }, 0);
};

/**
 * Inserts a variable into an autocomplete field (to/cc/bcc)
 */
const insertIntoAutocomplete = ({
  value,
  currentValues,
  onChange,
}: {
  value: string;
  currentValues: string[];
  onChange: (newValues: string[]) => void;
}) => {
  onChange([...currentValues, value]);
};

/**
 * Inserts a variable into a Draft.js WYSIWYG editor
 */
const insertIntoWysiwyg = ({
  value,
  editorState,
  onChange,
}: {
  value: string;
  editorState: EditorState;
  onChange: (newState: EditorState) => void;
}) => {
  const contentState = editorState.getCurrentContent();
  const selection = editorState.getSelection();

  const newContentState = Modifier.replaceText(contentState, selection, value);

  const newEditorState = EditorState.push(
    editorState,
    newContentState,
    'insert-characters'
  );

  onChange(newEditorState);
};

type InsertionHandlerParams =
  | {
      mode: 'textField';
      inputRef: React.RefObject<HTMLInputElement>;
      onChange: (newValue: string) => void;
      currentValue: string;
    }
  | {
      mode: 'autocomplete';
      currentValues: string[];
      onChange: (newValues: string[]) => void;
    }
  | {
      mode: 'wysiwyg';
      editorState: EditorState;
      onChange: (newState: EditorState) => void;
    };

export const handleInsert =
  (params: InsertionHandlerParams) => (value: string) => {
    switch (params.mode) {
      case 'textField':
        insertIntoTextField({
          value,
          inputRef: params.inputRef,
          onChange: params.onChange,
          currentValue: params.currentValue,
        });
        break;

      case 'autocomplete':
        insertIntoAutocomplete({
          value,
          currentValues: params.currentValues,
          onChange: params.onChange,
        });
        break;

      case 'wysiwyg':
        insertIntoWysiwyg({
          value,
          editorState: params.editorState,
          onChange: params.onChange,
        });
        break;

      default:
        // No-op for unknown modes
        break;
    }
  };
